import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useReduxState } from "hooks/useReduxState";
import {
  setPersonalLimit,
  setPersonalListLimit,
  setPersonalOffset,
  setPersonalSortValue,
  setPersonalKeyword,
  setPersonalSearchedKeyword,
  setIsSearch
} from "modules/binders/list/actions";
import { setPersonalCardViewMode } from "modules/binders/saved/actions";
import { useToast } from "hooks/useToast";
import { actionCreators as headerActionCreators } from "modules/header";
import { request } from "utils/apiClient";
import { useLocation } from "react-router";
import { LAZY_LOAD_LIMIT } from "features/BinderPage/List";
import { trackAction } from "utils/helper/analytics";
import { useMergeCollection } from "../../useMergeCollection";
import { useRequestMutation } from "hooks/useRequest";
import { BinderItem } from "models/BinderItem";

type ErrorType = {
  message: string;
  body: {
    code: number;
    details: {
      apiErrorCode: string;
      message: string;
      stackTrace?: string;
      errorType?: string;
    }[];
  };
};

const KEYWORD_MAX_LENGTH = 500;

export const usePersonalBinder = ({
  isHome = false
}: { isHome?: boolean } = {}) => {
  const dispatch = useDispatch();
  const offset = useReduxState(s => s.binders.list.personalOffset);
  const limit = useReduxState(s => s.binders.list.personalLimit);
  const listLimit = useReduxState(s => s.binders.list.personalListLimit);
  const sortValue = useReduxState(s => s.binders.list.personalSortValue);
  const personalKeyword =
    useReduxState(s => s.binders.list.personalKeyword) ?? "";
  // 並び替えなどの操作時は、入力中(未確定)の値ではなく、
  // 直前にAPIへリクエストしたキーワード(または空)でリクエストする
  // タブ切替で再マウントされても失われないよう、reduxに保持する
  const personalSearchedKeyword =
    useReduxState(s => s.binders.list.personalSearchedKeyword) ?? "";
  const isSearch = useReduxState(s => s.binders.list.isSearch);
  const cardViewMode = useReduxState(s => s.binders.saved.personalCardViewMode);
  const [isError, setIsError] = useState(false);
  const toast = useToast();
  const location = useLocation();

  const { trigger: fetchNikkeiCollectionTrigger } = useRequestMutation<{
    binderList: BinderItem[];
    totalCount: number;
  }>(`${isHome ? "/home" : ""}/binder/personal`, "GET");
  const {
    collections,
    isLoading,
    isAdding,
    totalCount,
    fetchCollections,
    addCollection
  } = useMergeCollection(
    fetchNikkeiCollectionTrigger,
    isHome ? "home" : "personal"
  );

  const fetchErrorHandle = useCallback(
    (error: ErrorType) => {
      const errors = [];
      if (error && error.body && error.body.details) {
        for (const detail of error.body.details) {
          errors.push(detail.message);
        }
      }
      toast.setErrors(
        error.message === "GATEWAY_TIMEOUT"
          ? "コレクションの読み込みがタイムアウトしました"
          : errors
      );
      setIsError(error.message === "GATEWAY_TIMEOUT");
    },
    [toast]
  );

  const fetchPersonalBinders = useCallback(
    async (searchLimit: number) => {
      const setLimit = cardViewMode
        ? LAZY_LOAD_LIMIT.card
        : searchLimit
        ? searchLimit
        : limit;
      try {
        dispatch(setPersonalOffset(0));
        dispatch(setPersonalLimit(setLimit));
        dispatch(setIsSearch(!!personalSearchedKeyword));
        dispatch(
          setPersonalSortValue(
            !personalSearchedKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: location.pathname === "/" || cardViewMode ? 1 : 0,
          offset: 0,
          limit: setLimit,
          sortKbn:
            !personalSearchedKeyword && sortValue === "3" ? "1" : sortValue,
          keyword: personalSearchedKeyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      cardViewMode,
      dispatch,
      fetchCollections,
      fetchErrorHandle,
      limit,
      location.pathname,
      personalSearchedKeyword,
      sortValue
    ]
  );

  const addPersonalBinders = useCallback(async () => {
    if (offset + limit < totalCount) {
      dispatch(setPersonalOffset(offset + limit));
      addCollection({
        needFeedArticles: cardViewMode ? 1 : 0,
        limit,
        offset: offset + limit,
        sortKbn: sortValue,
        keyword: personalSearchedKeyword
      });
    }
  }, [
    cardViewMode,
    dispatch,
    addCollection,
    limit,
    offset,
    personalSearchedKeyword,
    sortValue,
    totalCount
  ]);

  const sortPersonalBinder = async (sortKbn: string) => {
    try {
      dispatch(setPersonalOffset(0));
      dispatch(setIsSearch(!!personalSearchedKeyword));
      dispatch(
        setPersonalSortValue(
          !personalSearchedKeyword && sortKbn === "3" ? "1" : sortKbn
        )
      );
      fetchCollections({
        needFeedArticles: cardViewMode ? 1 : 0,
        sortKbn,
        offset: 0,
        limit,
        keyword: personalSearchedKeyword
      });
    } catch (error) {
      fetchErrorHandle(error as ErrorType);
    }
  };

  const deletePersonalBinder = async (binderId: string) => {
    try {
      const response: { binderId: string } = await request(
        "/binder/delete",
        { binderId },
        "POST"
      );
      dispatch(
        headerActionCreators.requestUpdate({ shouldLoadCollection: true })
      );
      return { success: true, binderId: response ? response.binderId : "" };
    } catch (error) {
      return {
        success: false,
        messages:
          (error as ErrorType).body?.details?.map(
            (item: any) => item.message
          ) ?? ""
      };
    }
  };

  const setCardViewMode = (isCardMode: boolean) => {
    dispatch(setPersonalCardViewMode(isCardMode));
  };

  const switchCardViewMode = useCallback(
    async (cardMode: boolean) => {
      try {
        const changedLimit = cardMode ? LAZY_LOAD_LIMIT.card : listLimit;
        if (!cardMode) {
          dispatch(setPersonalListLimit(changedLimit));
        }
        dispatch(setPersonalLimit(changedLimit));
        dispatch(setPersonalCardViewMode(cardMode));
        dispatch(setPersonalOffset(0));
        dispatch(setIsSearch(!!personalSearchedKeyword));
        dispatch(
          setPersonalSortValue(
            !personalSearchedKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: cardMode ? 1 : 0,
          sortKbn:
            !personalSearchedKeyword && sortValue === "3" ? "1" : sortValue,
          limit: changedLimit,
          keyword: personalSearchedKeyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      dispatch,
      fetchCollections,
      fetchErrorHandle,
      listLimit,
      personalSearchedKeyword,
      sortValue
    ]
  );

  const changePage = useCallback(
    async (offset: number, limit: number) => {
      try {
        dispatch(setPersonalOffset(offset));
        dispatch(setPersonalLimit(limit));
        dispatch(setIsSearch(!!personalSearchedKeyword));
        dispatch(
          setPersonalSortValue(
            !personalSearchedKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: cardViewMode ? 1 : 0,
          offset,
          limit,
          sortKbn:
            !personalSearchedKeyword && sortValue === "3" ? "1" : sortValue,
          keyword: personalSearchedKeyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      dispatch,
      personalSearchedKeyword,
      sortValue,
      fetchCollections,
      cardViewMode,
      fetchErrorHandle
    ]
  );

  const changeLimit = useCallback(
    async (limit: number) => {
      try {
        dispatch(setPersonalLimit(limit));
        dispatch(setPersonalListLimit(limit));
        dispatch(setPersonalOffset(0));
        dispatch(setIsSearch(!!personalSearchedKeyword));
        dispatch(
          setPersonalSortValue(
            !personalSearchedKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: cardViewMode ? 1 : 0,
          offset: 0,
          limit,
          sortKbn:
            !personalSearchedKeyword && sortValue === "3" ? "1" : sortValue,
          keyword: personalSearchedKeyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      cardViewMode,
      dispatch,
      fetchCollections,
      fetchErrorHandle,
      personalSearchedKeyword,
      sortValue
    ]
  );

  const onKeywordSearch = useCallback(
    async (keyword: string) => {
      // 500文字を超える場合はAPIを実行しない
      if (keyword.length > KEYWORD_MAX_LENGTH) return;
      try {
        trackAction("myCollectionSearchKeyword", keyword);
        dispatch(setPersonalKeyword(keyword));
        dispatch(setPersonalSearchedKeyword(keyword));
        dispatch(setIsSearch(!!keyword));
        dispatch(setPersonalOffset(0));
        dispatch(setPersonalSortValue(keyword ? "3" : "1"));
        fetchCollections({
          needFeedArticles: cardViewMode ? 1 : 0,
          offset: 0,
          limit,
          sortKbn: keyword ? "3" : "1",
          keyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [dispatch, fetchCollections, cardViewMode, limit, fetchErrorHandle]
  );

  return {
    isLoading,
    sortValue,
    isError,
    binderItems: collections,
    totalCount,
    fetchPersonalBinders,
    sortPersonalBinder,
    deletePersonalBinder,
    setCardViewMode,
    cardViewMode,
    addPersonalBinders,
    isAdding,
    limit,
    offset,
    switchCardViewMode,
    changePage,
    changeLimit,
    onKeywordSearch,
    personalKeyword,
    setPersonalKeyword,
    isSearch
  };
};
